import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { SunIcon } from "@phosphor-icons/react";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import LinkedSection from "../../components/crm/LinkedSection";
import InlineEditableField from "../../components/crm/InlineEditableField";
import CopyButton from "../../components/CopyButton";
import {
  ConfirmDelete,
  ErrorBanner,
  Field,
  Modal,
  NumberInput,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextArea,
  TextInput,
  DateInput,
} from "../../components/crm/Form";
import { FieldCard } from "./CustomerDetail";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const WRITE_ROLES = ["owner", "ops"];
const REGION_OPTIONS = [
  { value: "st_george", label: "St. George" },
  { value: "las_vegas", label: "Las Vegas" },
];
const FINANCING_OPTIONS = [
  { value: "cash", label: "Cash" },
  { value: "loan", label: "Loan" },
  { value: "lease", label: "Lease" },
];

export default function PropertyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const isTechAssistant = user?.role === "tech_assistant";

  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [sysOpen, setSysOpen] = useState(false);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get(`/properties/${id}`);
      setDoc(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, [id]);

  const handleDelete = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/properties/${id}`);
      nav("/properties");
    } catch (e) {
      setErr(formatApiError(e));
      setDelBusy(false);
    }
  };

  if (!doc)
    return (
      <Layout title="Property">
        <ErrorBanner message={err} />
      </Layout>
    );

  // Customer name rendering: tech_assistant gets plain non-clickable text;
  // all other roles see a link to the customer detail page.
  const customerNode = isTechAssistant ? (
    <span data-testid="customer-name-readonly" className="text-slate-700">
      {doc.customer_name || "—"}
    </span>
  ) : (
    <Link
      to={`/customers/${doc.customer_id}`}
      className="text-slate-700 hover:text-amber-600 transition-colors"
      data-testid="customer-name-link"
    >
      {doc.customer_name || "—"}
    </Link>
  );

  return (
    <Layout title={doc.address} subtitle="Property record">
      <EntityHeader
        overline="property"
        title={doc.address}
        breadcrumb={[
          { label: "Properties", to: "/properties" },
          { label: doc.address },
        ]}
        meta={
          <>
            <span className="inline-flex items-center gap-1.5">
              id · {doc.id.slice(0, 8)}
              <CopyButton
                value={doc.id}
                label="Property ID"
                testId="property-copy-id"
              />
            </span>
            <span>zip · {doc.zip_code}</span>
            <span>region · {doc.region.replace("_", " ")}</span>
          </>
        }
        canEdit={canWrite}
        canDelete={canWrite}
        onEdit={() => setEditOpen(true)}
        onDelete={() => setDelOpen(true)}
      />

      <ErrorBanner message={err} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <div className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Customer
          </div>
          <div className="mt-1.5 text-sm">{customerNode}</div>
        </div>
        <InlineEditableField
          label="Region"
          value={doc.region}
          name="region"
          mono
          as="select"
          options={REGION_OPTIONS}
          canEdit={canWrite}
          endpoint={`/properties/${id}`}
          onSaved={(d) => setDoc({ ...doc, ...d })}
          format={(v) => v?.replace("_", " ")}
          testIdPrefix="property-inline"
        />
        <FieldCard
          label="Solar Systems"
          value={String(doc.solar_systems.length)}
          mono
        />
        <InlineEditableField
          label="Address"
          value={doc.address}
          name="address"
          canEdit={canWrite}
          endpoint={`/properties/${id}`}
          placeholder="Add address"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="property-inline"
          className="sm:col-span-2"
        />
        <InlineEditableField
          label="ZIP"
          value={doc.zip_code}
          name="zip_code"
          mono
          canEdit={canWrite}
          endpoint={`/properties/${id}`}
          placeholder="Add ZIP"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="property-inline"
        />
        <InlineEditableField
          label="Notes"
          value={doc.notes}
          name="notes"
          as="textarea"
          canEdit={canWrite}
          endpoint={`/properties/${id}`}
          placeholder="Add notes"
          onSaved={(d) => setDoc({ ...doc, ...d })}
          testIdPrefix="property-inline"
          className="sm:col-span-2 lg:col-span-3"
        />
      </div>

      <LinkedSection
        title="Solar Systems"
        subtitle="installed at this address"
        count={doc.solar_systems.length}
        canCreate={canWrite}
        onCreate={() => setSysOpen(true)}
        addLabel="Add System"
        emptyTitle="No solar systems yet"
        emptyDescription="Add the first installed system at this property."
        data-testid="property-systems-section"
      >
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="font-data text-[10px] uppercase tracking-[0.18em] text-slate-600">
              <th className="text-left px-5 py-3 font-bold">Installed</th>
              <th className="text-left px-5 py-3 font-bold">Installer</th>
              <th className="text-left px-5 py-3 font-bold">Financing</th>
              <th className="text-left px-5 py-3 font-bold">Utility</th>
              <th className="text-left px-5 py-3 font-bold">Warranty</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {doc.solar_systems.map((s) => (
              <tr
                key={s.id}
                className="cursor-pointer hover:bg-slate-50/60"
                onClick={() => nav(`/solar-systems/${s.id}`)}
                data-testid={`system-row-${s.id}`}
              >
                <td className="px-5 py-3 font-medium text-slate-900 flex items-center gap-2">
                  <SunIcon
                    size={14}
                    weight="duotone"
                    className="text-amber-500"
                  />
                  {s.install_year}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  {s.original_installer || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs uppercase tracking-[0.1em] text-slate-700">
                  {s.financing_type}
                </td>
                <td className="px-5 py-3 text-xs text-slate-700">
                  {s.utility_company || "—"}
                </td>
                <td className="px-5 py-3 font-data text-xs text-slate-700 tabular">
                  {s.warranty_expires || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </LinkedSection>

      {canWrite && (
        <>
          <EditPropertyModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            property={doc}
            onSaved={(d) => {
              setEditOpen(false);
              setDoc({ ...doc, ...d });
            }}
          />
          <ConfirmDelete
            open={delOpen}
            onClose={() => setDelOpen(false)}
            onConfirm={handleDelete}
            label="Property"
            busy={delBusy}
          />
          <CreateSystemModal
            open={sysOpen}
            onClose={() => setSysOpen(false)}
            propertyId={doc.id}
            onCreated={() => {
              setSysOpen(false);
              load();
            }}
          />
        </>
      )}
    </Layout>
  );
}

function EditPropertyModal({ open, onClose, property, onSaved }) {
  const [form, setForm] = useState({});
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        address: property.address,
        zip_code: property.zip_code,
        region: property.region,
        notes: property.notes || "",
      });
      setErr("");
    }
  }, [open, property]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await api.patch(`/properties/${property.id}`, {
        address: form.address,
        zip_code: form.zip_code,
        region: form.region,
        notes: form.notes || null,
      });
      onSaved(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit Property"
      subtitle="update record"
      data-testid="property-edit-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Address" required>
          <TextInput
            required
            value={form.address}
            onChange={(v) => setForm({ ...form, address: v })}
            data-testid="property-edit-address"
          />
        </Field>
        <Field label="ZIP" required>
          <TextInput
            required
            value={form.zip_code}
            onChange={(v) => setForm({ ...form, zip_code: v })}
            data-testid="property-edit-zip"
          />
        </Field>
        <Field label="Region" required>
          <Select
            value={form.region}
            onChange={(v) => setForm({ ...form, region: v || "st_george" })}
            options={REGION_OPTIONS}
            placeholder=""
            data-testid="property-edit-region"
          />
        </Field>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            data-testid="property-edit-notes"
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="property-edit-submit"
          >
            {busy ? "Saving…" : "Save"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

function CreateSystemModal({ open, onClose, propertyId, onCreated }) {
  const [form, setForm] = useState({
    install_year: new Date().getFullYear(),
    original_installer: "",
    financing_type: "cash",
    utility_company: "",
    warranty_expires: "",
    notes: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        install_year: new Date().getFullYear(),
        original_installer: "",
        financing_type: "cash",
        utility_company: "",
        warranty_expires: "",
        notes: "",
      });
      setErr("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        property_id: propertyId,
        install_year: Number(form.install_year),
        financing_type: form.financing_type,
      };
      if (form.original_installer)
        payload.original_installer = form.original_installer;
      if (form.utility_company) payload.utility_company = form.utility_company;
      if (form.warranty_expires)
        payload.warranty_expires = form.warranty_expires;
      if (form.notes) payload.notes = form.notes;
      const { data } = await api.post("/solar-systems/", payload);
      onCreated(data);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Solar System"
      subtitle="install record"
      data-testid="system-create-modal"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Field label="Install Year" required>
            <NumberInput
              required
              min={1990}
              max={2100}
              value={form.install_year}
              onChange={(v) => setForm({ ...form, install_year: v })}
              data-testid="system-install-year"
            />
          </Field>
          <Field label="Financing" required>
            <Select
              value={form.financing_type}
              onChange={(v) =>
                setForm({ ...form, financing_type: v || "cash" })
              }
              options={FINANCING_OPTIONS}
              placeholder=""
              data-testid="system-financing"
            />
          </Field>
        </div>
        <Field label="Original Installer">
          <TextInput
            value={form.original_installer}
            onChange={(v) => setForm({ ...form, original_installer: v })}
            data-testid="system-installer"
          />
        </Field>
        <Field label="Utility Company">
          <TextInput
            value={form.utility_company}
            onChange={(v) => setForm({ ...form, utility_company: v })}
            data-testid="system-utility"
          />
        </Field>
        <Field label="System Warranty Expires">
          <DateInput
            value={form.warranty_expires}
            onChange={(v) => setForm({ ...form, warranty_expires: v })}
            data-testid="system-warranty-expires"
          />
        </Field>
        <Field label="Notes">
          <TextArea
            value={form.notes}
            onChange={(v) => setForm({ ...form, notes: v })}
            data-testid="system-notes"
          />
        </Field>
        <ErrorBanner message={err} />
        <div className="flex justify-end gap-2 pt-2">
          <SecondaryButton type="button" onClick={onClose}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            type="submit"
            disabled={busy}
            data-testid="system-submit"
          >
            {busy ? "Adding…" : "Add System"}
          </PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}
